import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import type { Tables } from '../lib/supabase/types';
import { supabase } from '../lib/supabase/client';
import { fetchUserProfile, updateUserProfile, uploadProfileImage, deleteProfileImage, type ProfileUpdateData } from '../lib/profile/profile-service';

type UserProfile = Tables<'user_profile'>;

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  profileLoaded: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  loadProfile: (userId: string) => Promise<void>;
  updateProfile: (userId: string, data: ProfileUpdateData) => Promise<{ success: boolean; error?: string }>;
  uploadImage: (userId: string, file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  removeImage: (userId: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => void;
  reset: () => void;

  // Verification status helpers
  isEmailVerified: () => boolean;
  isPhoneVerified: () => boolean;
  hasPassword: () => boolean;
  isPartner: () => boolean;
  refreshUser: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const initialState = {
  user: null,
  session: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,
  profileLoaded: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setSession: (session) => set({ session }),

      setProfile: (profile) => set({ profile, profileLoaded: true }),

      setLoading: (isLoading) => set({ isLoading }),

      loadProfile: async (userId: string) => {
        // Check if already loaded
        const state = get();

        if (state.profileLoaded && state.profile?.user_id === userId) {
          return; // Already loaded
        }

        // Never put a known session back into the global initializing state.
        // AppHeader used to swap authenticated nav for a skeleton / guest CTAs.
        if (!state.isAuthenticated && !state.user) {
          set({ isLoading: true });
        }

        // Helper function to attempt profile fetch with retries
        const fetchWithRetry = async (retries = 3, delay = 500): Promise<{ data: any; error: any }> => {
          for (let i = 0; i < retries; i++) {
            const result = await fetchUserProfile(userId);

            if (result.data) {
              return result;
            }

            // If not the last retry, wait before trying again
            if (i < retries - 1) {
              await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
            }
          }

          // Return the last attempt result
          return await fetchUserProfile(userId);
        };

        const { data, error } = await fetchWithRetry();

        if (error) {

          // If profile not found, check if this is a partner (their profile may be delayed
          // while the DB trigger runs). For regular users, set profile to null and let the
          // profile-completion guard redirect them to /profile/complete.
          if (error.message === 'Profile not found') {
            let isPartner = false;
            try {
              const { data } = await supabase
                .from('partners' as any)
                .select('id')
                .eq('user_id', userId)
                .maybeSingle();
              isPartner = !!data;
            } catch {
              // Ignore — assume not a partner
            }

            if (isPartner) {
              // Partner profile is created by a DB trigger; give it a moment and retry once.
              await new Promise(resolve => setTimeout(resolve, 1000));
              const { data: retryData } = await fetchUserProfile(userId);
              if (retryData) {
                set({ profile: retryData, isLoading: false, profileLoaded: true });
                return;
              }
            }

            // Regular user with no profile: surface as null so the completion guard
            // can redirect them to /profile/complete.
            set({ profile: null, isLoading: false, profileLoaded: true });
            return;
          }

          set({ isLoading: false, profileLoaded: false });
          return;
        }

        set({ profile: data, isLoading: false, profileLoaded: true });
      },

      updateProfile: async (userId: string, data: ProfileUpdateData) => {
        const { data: updated, error } = await updateUserProfile(userId, data);

        if (error) {
          return { success: false, error: error.message };
        }

        // Update store with new data
        set({ profile: updated });
        return { success: true };
      },

      uploadImage: async (userId: string, file: File) => {
        const { url, error } = await uploadProfileImage(userId, file);

        if (error) {
          return { success: false, error: error.message };
        }

        // Update profile with new image URL
        const { data: updated, error: updateError } = await updateUserProfile(userId, {
          image_url: url || undefined,
        });

        if (updateError) {
          return { success: false, error: updateError.message };
        }

        set({ profile: updated });
        return { success: true, url: url || undefined };
      },

      removeImage: async (userId: string) => {
        const { error: deleteError } = await deleteProfileImage(userId);

        if (deleteError) {
          return { success: false, error: deleteError.message };
        }

        // Update profile to remove image URL
        const { data: updated, error: updateError } = await updateUserProfile(userId, {
          image_url: null as any,
        });

        if (updateError) {
          return { success: false, error: updateError.message };
        }

        set({ profile: updated });
        return { success: true };
      },

      signOut: () => {
        // Clean up rememberMe preference on sign out
        localStorage.removeItem('candle-remember-me');
        set(initialState);
      },

      reset: () => set(initialState),

      // Verification status helpers
      isEmailVerified: () => {
        const { user } = get();
        return !!(user?.email_confirmed_at);
      },

      isPhoneVerified: () => {
        const { user } = get();
        return !!(user?.phone_confirmed_at);
      },

      hasPassword: () => {
        const { user } = get();
        // Users who signed up with email+password have email_confirmed_at set
        // This is a heuristic since Supabase doesn't expose password existence directly
        return !!(user?.email && user?.email_confirmed_at);
      },

      isPartner: () => {
        const { profile } = get();
        return (profile as any)?.user_type === 'partner';
      },

      refreshUser: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          set({ user, isAuthenticated: true });
        }
      },

      refreshProfile: async () => {
        const { user } = get();
        if (!user) return;
        const { data } = await fetchUserProfile(user.id);
        if (data) {
          set({ profile: data, profileLoaded: true });
        }
      },
    }),
    {
      name: 'candle-auth-storage',
      partialize: (state) => ({
        profile: state.profile,
        profileLoaded: state.profileLoaded,
      }),
    }
  )
);
